import api from './api';

export type IvaCondition = 'monotributo' | 'responsable_inscripto' | 'exento';
export type ArcaEnvironment = 'homologacion' | 'produccion';

export interface BillingSettings {
  id: number;
  tenant_id: number;
  cuit: string | null;
  razon_social: string | null;
  iva_condition: IvaCondition | null;
  environment: ArcaEnvironment;
  has_certificate: boolean;
  has_private_key: boolean;
  is_configured: boolean;
  created_at: string;
  updated_at?: string;
}

export interface BillingSettingsUpdate {
  cuit?: string | null;
  razon_social?: string | null;
  iva_condition?: IvaCondition | null;
  environment?: ArcaEnvironment;
}

// A que rubro factura un punto de venta. Algunas veterinarias declaran uno por rubro
// porque los servicios estan exentos de Ingresos Brutos y los productos no.
export type PointOfSaleAppliesTo = 'all' | 'services' | 'products';

export const APPLIES_TO_LABELS: Record<PointOfSaleAppliesTo, string> = {
  all: 'Todo',
  services: 'Servicios',
  products: 'Productos',
};

export interface PointOfSale {
  id: number;
  billing_settings_id: number;
  number: number;
  description: string;
  is_active: boolean;
  applies_to: PointOfSaleAppliesTo;
  created_at: string;
  updated_at?: string;
}

export interface PointOfSaleCreate {
  number: number;
  description: string;
  is_active?: boolean;
  applies_to?: PointOfSaleAppliesTo;
}

export interface PointOfSaleUpdate {
  number?: number;
  description?: string;
  is_active?: boolean;
  applies_to?: PointOfSaleAppliesTo;
}

export interface BillingTestCheck {
  step: string;
  ok: boolean;
  detail: string;
}

export interface BillingTestResult {
  ok: boolean;
  ambiente: ArcaEnvironment;
  checks: BillingTestCheck[];
}

export type InvoiceType =
  | 'factura_a' | 'factura_b' | 'factura_c'
  | 'nota_credito_a' | 'nota_credito_b' | 'nota_credito_c'
  | 'nota_debito_a' | 'nota_debito_b' | 'nota_debito_c';

export type InvoiceStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'error';
export type DocumentType = 'cuit' | 'dni' | 'sin_documento';
export type ReceiverIvaCondition =
  | 'consumidor_final' | 'monotributo' | 'responsable_inscripto' | 'exento' | 'no_categorizado';

export interface Invoice {
  id: number;
  tenant_id: number;
  sale_id: number | null;
  point_of_sale_id: number;
  invoice_type: InvoiceType;
  pos_number: number;
  invoice_number: number | null;
  receiver_doc_type: DocumentType;
  receiver_doc_number: string | null;
  receiver_name: string | null;
  receiver_iva_condition: ReceiverIvaCondition;
  receiver_address: string | null;
  subtotal: string;
  iva_amount: string;
  total: string;
  cae: string | null;
  cae_due_date: string | null;
  status: InvoiceStatus;
  error_message: string | null;
  invoice_date: string;
  created_at: string;
  updated_at?: string;
  notes: string | null;
  // Si es nota de credito, la factura que anula
  credited_invoice_id: number | null;
}

export interface InvoiceDetail extends Invoice {
  qr_url: string | null;
  emisor_cuit: string | null;
  emisor_razon_social: string | null;
  emisor_iva_condition: string | null;
}

export interface InvoiceFromSaleRequest {
  point_of_sale_id: number;
  receiver_name?: string | null;
  receiver_doc_type?: DocumentType;
  receiver_doc_number?: string | null;
  receiver_iva_condition?: ReceiverIvaCondition;
  receiver_address?: string | null;
}

// La letra sale del tipo de comprobante: factura_c -> "C".
export const invoiceLetter = (t: InvoiceType): string => t.slice(-1).toUpperCase();

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  factura_a: 'Factura A',
  factura_b: 'Factura B',
  factura_c: 'Factura C',
  nota_credito_a: 'Nota de Crédito A',
  nota_credito_b: 'Nota de Crédito B',
  nota_credito_c: 'Nota de Crédito C',
  nota_debito_a: 'Nota de Débito A',
  nota_debito_b: 'Nota de Débito B',
  nota_debito_c: 'Nota de Débito C',
};

export interface BillingSummaryRow {
  point_of_sale_id: number;
  pos_number: number;
  description: string;
  applies_to: PointOfSaleAppliesTo;
  invoices_count: number;
  invoices_total: string;
  credit_notes_count: number;
  credit_notes_total: string;
  net_total: string;
}

export interface BillingSummaryMonth {
  month: number;
  net_total: string;
}

export interface BillingSummary {
  year: number;
  month: number | null;
  by_point_of_sale: BillingSummaryRow[];
  by_month: BillingSummaryMonth[];
  net_total: string;
}

export const billingService = {
  async getSettings(): Promise<BillingSettings> {
    const { data } = await api.get<BillingSettings>('/billing/settings');
    return data;
  },

  async updateSettings(payload: BillingSettingsUpdate): Promise<BillingSettings> {
    const { data } = await api.put<BillingSettings>('/billing/settings', payload);
    return data;
  },

  async uploadCertificate(file: File): Promise<BillingSettings> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<BillingSettings>('/billing/certificate', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async uploadPrivateKey(file: File): Promise<BillingSettings> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<BillingSettings>('/billing/private-key', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async testConnection(): Promise<BillingTestResult> {
    const { data } = await api.get('/billing/test-connection');
    return data;
  },

  // Devuelve una lista: una venta mixta puede generar dos comprobantes si la
  // veterinaria separa servicios de productos en distintos puntos de venta.
  async createInvoiceFromSale(saleId: number, payload: InvoiceFromSaleRequest): Promise<Invoice[]> {
    const { data } = await api.post<Invoice[]>(`/billing/invoices/from-sale/${saleId}`, payload);
    return data;
  },

  async createCreditNote(invoiceId: number, payload: { total?: number; motivo?: string }): Promise<Invoice> {
    const { data } = await api.post<Invoice>(`/billing/invoices/${invoiceId}/credit-note`, payload);
    return data;
  },

  async listInvoices(params?: {
    saleId?: number;
    year?: number;
    month?: number;
    pointOfSaleId?: number;
    onlyApproved?: boolean;
  }): Promise<Invoice[]> {
    const { data } = await api.get<Invoice[]>('/billing/invoices', {
      params: {
        ...(params?.saleId ? { sale_id: params.saleId } : {}),
        ...(params?.year ? { year: params.year } : {}),
        ...(params?.month ? { month: params.month } : {}),
        ...(params?.pointOfSaleId ? { point_of_sale_id: params.pointOfSaleId } : {}),
        ...(params?.onlyApproved ? { only_approved: true } : {}),
      },
    });
    return data;
  },

  async getInvoice(id: number): Promise<InvoiceDetail> {
    const { data } = await api.get<InvoiceDetail>(`/billing/invoices/${id}`);
    return data;
  },

  async getSummary(year: number, month?: number): Promise<BillingSummary> {
    const { data } = await api.get<BillingSummary>('/billing/summary', {
      params: { year, ...(month ? { month } : {}) },
    });
    return data;
  },

  async listPointsOfSale(): Promise<PointOfSale[]> {
    const { data } = await api.get<PointOfSale[]>('/billing/points-of-sale');
    return data;
  },

  async createPointOfSale(payload: PointOfSaleCreate): Promise<PointOfSale> {
    const { data } = await api.post<PointOfSale>('/billing/points-of-sale', payload);
    return data;
  },

  async updatePointOfSale(id: number, payload: PointOfSaleUpdate): Promise<PointOfSale> {
    const { data } = await api.put<PointOfSale>(`/billing/points-of-sale/${id}`, payload);
    return data;
  },

  async deletePointOfSale(id: number): Promise<void> {
    await api.delete(`/billing/points-of-sale/${id}`);
  },
};
